import React from 'react';
import { PrimaryNavItem } from './PrimaryNavItem';
import { DropdownPanel } from './DropdownPanel';
import { PrimaryNavGroup, NavFooterItem } from './MegaMenu';

export interface DesktopNavProps {
  /** Pre-built logo element from MegaMenu shell */
  logoSlot?: React.ReactNode;
  /** Pre-built CTA buttons from MegaMenu shell */
  ctaSlot?: React.ReactNode;

  /** L1 navigation groups rendered as menu items */
  primaryNavGroups: PrimaryNavGroup[];
  /** Footer links shown below dropdown panel */
  navFooterItems?: NavFooterItem[];
  /** Currently selected/expanded nav group ID */
  selectedPrimaryNavGroupId: string;
  /** Toggle expand/collapse of a nav group by ID */
  toggleSelectedPrimaryNavGroupId: (id: string) => void;
  /** Close all open dropdown panels */
  closeAllPanels: () => void;
  /** Keyboard handler for nav container (Escape, arrow keys) */
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

/**
 * DesktopNav — Desktop navigation view for MegaMenu
 *
 * Renders the desktop layout: logo, primary nav list, dropdown panel,
 * and CTA action buttons. All state is managed by the parent MegaMenu
 * shell; this component is purely presentational.
 */
export const DesktopNav: React.FC<DesktopNavProps> = ({
  logoSlot,
  ctaSlot,
  primaryNavGroups,
  navFooterItems,
  selectedPrimaryNavGroupId,
  toggleSelectedPrimaryNavGroupId,
  closeAllPanels,
  handleKeyDown,
}) => (
  <div className="poly-mega-menu__desktop">
    <div className="poly-mega-menu__nav-container" onKeyDown={handleKeyDown}>
      {/* Logo */}
      {logoSlot}

      {/* Primary Navigation List (L1 Items) */}
      {Array.isArray(primaryNavGroups) && primaryNavGroups.length > 0 && (
        <ul className={`poly-mega-menu__primary-nav${selectedPrimaryNavGroupId ? ' poly-mega-menu__primary-nav--has-selected' : ''}`} role="menubar">
          {primaryNavGroups.map((navGroup) => {
            const isDirectLink = navGroup.isDirectLink ||
              (navGroup.secondaryNavGroups?.length === 0 && !!navGroup.directLinkUrl);

            return (
              <PrimaryNavItem
                key={navGroup.id}
                title={navGroup.title}
                id={navGroup.id}
                isSelected={selectedPrimaryNavGroupId === navGroup.id}
                onClick={() => toggleSelectedPrimaryNavGroupId(navGroup.id)}
                isDirectLink={!!isDirectLink}
                directLinkUrl={navGroup.directLinkUrl}
                directLinkCtaId={navGroup.directLinkCtaId}
                role="menuitem"
              />
            );
          })}
        </ul>
      )}

      {/* Dropdown Panel — renders below nav bar for selected L1 item */}
      {(() => {
        const selected = primaryNavGroups.find(g => g.id === selectedPrimaryNavGroupId);
        if (!selected || selected.isDirectLink) return null;
        return (
          <DropdownPanel
            isOpen={true}
            linkColumns={selected.secondaryNavGroups}
            featuredImageUrl={selected.featuredImageUrl}
            featuredUrl={selected.featuredUrl}
            featuredCtaId={selected.featuredId}
            featuredTitle={selected.featuredTitle}
            featuredDescription={selected.featuredDescription}
            featuredBadgeText={selected.featuredBadgeText}
            featuredBadgeIconUrl={selected.featuredBadgeIconUrl}
            featuredVideoUrl={selected.featuredVideoUrl}
            featuredSize={selected.featuredSize}
            footerItems={navFooterItems}
            onClose={closeAllPanels}
            panelId={`panel-${selected.id}`}
          />
        );
      })()}

      {/* Right side: CTAs */}
      {ctaSlot}
    </div>
  </div>
);
